import React, { useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import ProForm, {
  ModalForm,
  ProFormDependency,
  ProFormSelect,
} from '@ant-design/pro-form';
import { Button, Empty } from 'antd';
import { debounce, trim } from 'lodash';
import { useQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import { SEARCH_USERS } from 'gql/users';
import {
  SelectFaculty,
  SelectDepartment,
} from 'components/input/SelectFacultyDepartment';
import { isEmpty } from 'utils';
import { selectUserOptionItemRender } from 'components/input/SelectUser';

const getOptions = (searchUsers) => {
  return searchUsers?.map((user) => {
    if (user.__typename === 'User') {
      return {
        user: user,
        label: `${user.name} [${user.department?.abbreviation}]`,
        value: `${user.name} [${user.department?.abbreviation}]`,
      };
    }
    return {
      user: user,
      label: `${user.hroname} [${user.department?.abbreviation}]`,
      value: `${user.hroname} [${user.department?.abbreviation}]`,
      email: user.hroemail,
    };
  });
};

const AddTeamMemberModal = ({ actionRef, disabled }) => {
  const [form] = ProForm.useForm();
  const [selectedUser, setSelectedUser] = useState(null);
  const [options, setOptions] = useState([]);
  const [visible, setVisible] = useState(false);
  const [notFoundContent, setNotFoundContent] = useState(
    <Empty description="Type to search" />
  );
  const { refetch } = useQuery(SEARCH_USERS, { variables: { first: 1 } });
  const onSearch =
    ({ department, faculty }) =>
    async (searchText) => {
      if (!searchText) {
        setNotFoundContent(<Empty description="Type to search" />);
        return;
      }
      const { data: { searchUsers } = {} } = await refetch({
        department: department,
        faculty: faculty,
        keyword: trim(searchText),
        first: 10,
      });
      if (isEmpty(searchUsers)) {
        setNotFoundContent(<Empty description="No user found" />);
      }
      setOptions(getOptions(searchUsers));
    };
  const onSelect = (_value, option) => {
    setSelectedUser(option.user);
  };
  const onClear = () => {
    setSelectedUser(null);
    setNotFoundContent(<Empty description="Type to search" />);
    setOptions([]);
  };
  const addNew = () => {
    const department = form.getFieldValue('department');
    actionRef.current?.add({
      department,
    });
    setVisible(false);
  };

  return (
    <>
      <div></div>
      <ModalForm
        form={form}
        title="Add Team Member"
        trigger={
          <Button
            type="dashed"
            className="ant-btn-block ant-pro-form-list-creator-button-bottom"
            onClick={() => setVisible(true)}
            disabled={disabled}
          >
            <PlusOutlined />
            Add Team Member
          </Button>
        }
        visible={visible}
        modalProps={{
          onCancel: () => setVisible(false),
        }}
        onFinish={(values) => {
          if (values.name && selectedUser) {
            if (selectedUser.__typename === 'User') {
              actionRef.current.add({
                user: selectedUser.id,
                netId: selectedUser.netId,
                name: selectedUser.name,
                department: selectedUser.department?.id,
                email: selectedUser.email,
                position: selectedUser.userFromHroStaff?.position,
              });
            } else if (selectedUser.__typename === 'UserFromHroStaff') {
              actionRef.current.add({
                userFromHroStaff: selectedUser.id,
                netId: selectedUser.netId,
                name: selectedUser.hroname,
                department: selectedUser.department?.id,
                email: selectedUser.hroemail,
                position: selectedUser.position,
              });
            }
          } else {
            actionRef.current?.add({});
          }
          setVisible(false);
        }}
        onVisibleChange={(visible) => {
          if (visible) {
            form.resetFields();
            setSelectedUser(null);
            setOptions([]);
          }
        }}
      >
        <SelectFaculty rules={[]} form={form} />
        <SelectDepartment rules={[]} />
        <ProFormDependency name={['department', 'faculty']}>
          {({ department, faculty }) => (
            <ProFormSelect
              name="name"
              label="Name"
              showSearch
              allowClear
              options={options}
              style={{
                width: 552,
              }}
              fieldProps={{
                onSearch: debounce(onSearch({ department, faculty }), 300),
                onSelect,
                onClear,
                filterOption: false,
                notFoundContent,
                optionItemRender: selectUserOptionItemRender,
              }}
              placeholder="type to search team member"
            />
          )}
        </ProFormDependency>
        <Button type="primary" onClick={addNew}>
          Click here if you cannot find the name of the team member
        </Button>
      </ModalForm>
    </>
  );
};

AddTeamMemberModal.defaultProps = {
  disabled: false,
};

AddTeamMemberModal.propTypes = {
  actionRef: PropTypes.object.isRequired,
  disabled: PropTypes.bool.isRequired,
};

export default AddTeamMemberModal;
